import { useTranslation } from 'react-i18next';
import { useOrganizationUpgradePreview } from 'features/organizations/hooks/useOrganizationUpgradePreview';
import {
  Table,
  TableBody,
  tableBodyClasses,
  TableCell,
  tableCellClasses,
  TableRow,
  tableRowClasses,
  Typography,
} from '@mui/material';
import { formatToUSDollars } from 'shared/utils';
import { OrganizationUpgrade } from 'features/organizations/organizations.types';
import { useOrganizationDetails } from 'features/organizations/hooks/useOrganizationDetails';
import { InlineStatus } from 'components/InlineStatus';
import { EMPTY_DATA_VALUE } from '../../../../constants';

interface OrganizationUpgradeSummaryProps {
  regularSeats: number;
  proSeats: number;
}

const displayNumber = (num: number | undefined): string => {
  if (num === undefined) {
    return EMPTY_DATA_VALUE;
  }
  if (num < 0) {
    return `(${formatToUSDollars(-num, 2)})`;
  }

  return formatToUSDollars(num || 0, 2);
};

export const OrganizationUpgradeSummary = (
  props: OrganizationUpgradeSummaryProps,
): JSX.Element | null => {
  const { regularSeats = 0, proSeats = 0 } = props;
  const { t } = useTranslation();
  const {
    details,
    isLoading: detailsLoading,
    isError: detailsError,
  } = useOrganizationDetails();

  const { previewUpgrade, isLoading, isError } = useOrganizationUpgradePreview({
    regularSeats,
    proSeats,
  });

  const summary: Partial<OrganizationUpgrade> = previewUpgrade || {};

  if (isLoading || isError || detailsLoading || detailsError) {
    return (
      <InlineStatus
        status={isLoading || detailsLoading ? 'loading' : 'error'}
      />
    );
  }
  if (!(proSeats || regularSeats)) {
    return null;
  }

  const updatedSeats = {
    pro: {
      purchased: (details?.seats.pro.purchased || 0) + proSeats,
      used: details?.seats.pro.used || 0,
    },
    regular: {
      purchased: (details?.seats.regular.purchased || 0) + regularSeats,
      used: details?.seats.regular.used || 0,
    },
  };

  return (
    <Table
      size="small"
      sx={{
        [`& .${tableCellClasses.root}`]: {
          borderBottom: 'none',
          padding: 0,
          paddingBottom: 0.5,
          fontSize: 'inherit',
        },
        [`& .${tableBodyClasses.root} .${tableRowClasses.root}:first-child .${tableCellClasses.root}`]:
          {
            paddingTop: 2,
          },
      }}
    >
      <TableBody>
        <TableRow>
          <TableCell colSpan={3}>
            <Typography variant="h2">
              {t('organizationPurchase.total')}
            </Typography>
          </TableCell>
        </TableRow>
      </TableBody>
      <TableBody className="plan-type-summary">
        <TableRow>
          <TableCell>
            <b>{t('organizationPurchase.membership')}</b>
          </TableCell>
          <TableCell align="right">{updatedSeats.regular.purchased}</TableCell>
          <TableCell align="right">
            {displayNumber(summary.planTypeSubtotals?.regular)}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>
            <b>{t('organizationPurchase.pro')}</b>
          </TableCell>
          <TableCell align="right">{updatedSeats.pro.purchased}</TableCell>
          <TableCell align="right">
            {displayNumber(summary.planTypeSubtotals?.pro)}
          </TableCell>
        </TableRow>
      </TableBody>
      <TableBody>
        <TableRow>
          <TableCell />
          <TableCell align="right">
            {t('organizationPurchase.subtotal')}
          </TableCell>
          <TableCell align="right">{displayNumber(summary.subtotal)}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell />
          <TableCell align="right">
            {t('organizationPurchase.savings')}
          </TableCell>
          <TableCell align="right">{displayNumber(summary.savings)}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell />
          <TableCell align="right">
            <b>{t('organizationPurchase.total')}</b>
          </TableCell>
          <TableCell align="right">
            <b>{displayNumber(summary.total)}</b>
          </TableCell>
        </TableRow>
      </TableBody>
      <TableBody>
        <TableRow>
          <TableCell />
          <TableCell align="right">
            {t('organizationPurchase.credit')}
          </TableCell>
          <TableCell align="right">{displayNumber(summary.credit)}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell />
          <TableCell align="right">
            <b>{t('organizationPurchase.charge')}</b>
          </TableCell>
          <TableCell align="right">
            <b>{displayNumber(summary.charge)}</b>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};
